import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import Modal from '../../components/common/Modal';

// ═══════════════════════════════════════════════════════════════
// LINK TYPE CONFIG
// ═══════════════════════════════════════════════════════════════
const LINK_TYPES = {
  repayment: {
    label: '📥 Repayment — Money received but not linked to loan',
    description: 'Client paid via M-Pesa but the payment was NOT recorded in the system. Use this to manually link the M-Pesa code to the correct client/loan.',
    color: '#059669',
    bg: '#ecfdf5',
    border: '#34d399',
    icon: '📥',
  },
  disbursement: {
    label: '💸 Disbursement — Money sent but system shows as failed/pending',
    description: 'B2C disbursement was SUCCESSFUL on Safaricom portal (client confirmed receipt) but the system did NOT receive the callback. Use this to confirm and update the disbursement status.',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#60a5fa',
    icon: '💸',
  },
};

// ═══════════════════════════════════════════════════════════════
// STEP INDICATOR
// ═══════════════════════════════════════════════════════════════
const StepIndicator = ({ currentStep, steps }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
    {steps.map((step, idx) => {
      const isCompleted = idx < currentStep;
      const isCurrent = idx === currentStep;
      return (
        <React.Fragment key={idx}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flex: 1,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700,
              background: isCompleted ? '#059669' : (isCurrent ? '#2563eb' : '#e2e8f0'),
              color: isCompleted ? '#fff' : (isCurrent ? '#fff' : '#94a3b8'),
              transition: 'all 0.3s',
              flexShrink: 0,
            }}>
              {isCompleted ? '✓' : idx + 1}
            </div>
            <span style={{
              fontSize: 13, fontWeight: isCurrent ? 700 : 400,
              color: isCurrent ? '#1e40af' : (isCompleted ? '#059669' : '#94a3b8'),
              whiteSpace: 'nowrap',
            }}>
              {step}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div style={{
              width: 40, height: 2, flexShrink: 0,
              background: isCompleted ? '#059669' : '#e2e8f0',
              transition: 'all 0.3s',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function MpesaCodeLinking() {
  const navigate = useNavigate();

  // ─── Wizard State ───
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ['Select Type', 'Enter Code', 'Find Client', 'Confirm & Link'];

  // ─── Form State ───
  const [linkType, setLinkType] = useState('');
  const [mpesaCode, setMpesaCode] = useState('');
  const [mpesaAmount, setMpesaAmount] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [mpesaDate, setMpesaDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  // ─── Client Search State ───
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLoans, setClientLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);

  // ─── Pending Disbursements (for disbursement linking) ───
  const [pendingDisbursements, setPendingDisbursements] = useState([]);
  const [selectedDisbursement, setSelectedDisbursement] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);

  // ─── Submission State ───
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [result, setResult] = useState(null);

  // ═══════════════════════════════════════════════════════════
  // HANDLE TYPE SELECTION
  // ═══════════════════════════════════════════════════════════
  const handleTypeSelect = (type) => {
    setLinkType(type);
    setSelectedClient(null);
    setSelectedLoan(null);
    setSelectedDisbursement(null);
    setClientLoans([]);
    setPendingDisbursements([]);
    setCurrentStep(1);

    // If disbursement, fetch pending/failed disbursements
    if (type === 'disbursement') {
      fetchPendingDisbursements();
    }
  };

  // ═══════════════════════════════════════════════════════════
  // FETCH PENDING DISBURSEMENTS
  // ═══════════════════════════════════════════════════════════
  const fetchPendingDisbursements = async () => {
    setLoadingPending(true);
    try {
      const res = await api.get('/disbursements?status=failed&limit=50');
      setPendingDisbursements(res.data.data?.disbursements || res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch pending disbursements:', err);
    } finally {
      setLoadingPending(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // SEARCH CLIENT
  // ═══════════════════════════════════════════════════════════
  const searchClient = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(searchQuery.trim())}&limit=10`);
      setSearchResults(res.data.data?.clients || []);
    } catch (err) {
      console.error('Client search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2 && linkType === 'repayment') {
        searchClient();
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, searchClient, linkType]);

  // ═══════════════════════════════════════════════════════════
  // SELECT CLIENT → FETCH LOANS
  // ═══════════════════════════════════════════════════════════
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setSelectedLoan(null);
    setSearchResults([]);

    if (linkType === 'repayment') {
      try {
        const res = await api.get(`/loans?client=${client._id}&excludeClosed=true&limit=20`);
        setClientLoans(res.data.data?.loans || []);
      } catch (err) {
        console.error('Failed to fetch client loans:', err);
        setClientLoans([]);
      }
    }

    // Auto-fill phone if available
    if (client.phone && !mpesaPhone) {
      setMpesaPhone(client.phone);
    }

    setCurrentStep(3);
  };

  // ═══════════════════════════════════════════════════════════
  // VALIDATE & SUBMIT
  // ═══════════════════════════════════════════════════════════
  const canSubmit = () => {
    if (linkType === 'repayment') {
      return mpesaCode.trim() && mpesaAmount && parseFloat(mpesaAmount) > 0 &&
             selectedClient && selectedLoan;
    }
    if (linkType === 'disbursement') {
      return mpesaCode.trim() && selectedDisbursement;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    try {
      let res;
      if (linkType === 'repayment') {
        res = await api.post('/mpesa/link-code', {
          linkType: 'repayment',
          mpesaCode: mpesaCode.trim(),
          mpesaAmount: parseFloat(mpesaAmount),
          mpesaPhone: mpesaPhone.trim(),
          mpesaDate,
          clientId: selectedClient._id,
          loanId: selectedLoan._id,
          notes: notes.trim(),
        });
      } else {
        res = await api.post('/mpesa/link-code', {
          linkType: 'disbursement',
          mpesaCode: mpesaCode.trim(),
          disbursementId: selectedDisbursement._id,
          notes: notes.trim(),
        });
      }

      setResult(res.data.data);
      setShowSuccessModal(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to link code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // RESET
  // ═══════════════════════════════════════════════════════════
  const handleReset = () => {
    setCurrentStep(0);
    setLinkType('');
    setMpesaCode('');
    setMpesaAmount('');
    setMpesaPhone('');
    setMpesaDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedClient(null);
    setClientLoans([]);
    setSelectedLoan(null);
    setPendingDisbursements([]);
    setSelectedDisbursement(null);
    setResult(null);
    setShowSuccessModal(false);
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: STEP 0 — Select Link Type
  // ═══════════════════════════════════════════════════════════
  const renderStep0 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🔗</div>
        <h4 style={{ margin: '0 0 4px 0' }}>What type of transaction are you linking?</h4>
        <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
          Select whether this is a repayment that didn't hit the system, or a disbursement that succeeded but wasn't confirmed.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 700, margin: '0 auto' }}>
        {Object.entries(LINK_TYPES).map(([key, config]) => (
          <button
            key={key}
            onClick={() => handleTypeSelect(key)}
            style={{
              padding: 24,
              borderRadius: 12,
              border: `2px solid ${config.border}`,
              background: config.bg,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${config.color}22`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>{config.icon}</div>
            <div style={{ fontWeight: 700, color: config.color, fontSize: 15, marginBottom: 8 }}>
              {config.label}
            </div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              {config.description}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: STEP 1 — Enter M-Pesa Code
  // ═══════════════════════════════════════════════════════════
  const renderStep1 = () => (
    <div style={{ maxWidth: 500, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 12px', borderRadius: 20,
          background: LINK_TYPES[linkType].bg,
          border: `1px solid ${LINK_TYPES[linkType].border}`,
          fontSize: 12, fontWeight: 600, color: LINK_TYPES[linkType].color,
          marginBottom: 12,
        }}>
          {LINK_TYPES[linkType].icon} {LINK_TYPES[linkType].label.split('—')[0].trim()}
        </div>
        <h4 style={{ margin: '0 0 4px 0' }}>Enter M-Pesa Transaction Details</h4>
        <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
          Enter the M-Pesa transaction code from the client's SMS or your Safaricom portal.
        </p>
      </div>

      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
            M-Pesa Transaction Code * <span style={{ fontWeight: 400, color: '#94a3b8' }}>(e.g., QJK3LR2P5V)</span>
          </label>
          <input
            className="form-control"
            style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}
            value={mpesaCode}
            onChange={e => setMpesaCode(e.target.value.toUpperCase())}
            placeholder="Enter 10-character code"
            maxLength={12}
            autoFocus
          />
        </div>

        {linkType === 'repayment' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Amount (KES) *
              </label>
              <input
                type="number"
                className="form-control"
                style={{ fontSize: 16, fontWeight: 700 }}
                value={mpesaAmount}
                onChange={e => setMpesaAmount(e.target.value)}
                placeholder="Amount from M-Pesa SMS"
                min="1"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Sender Phone <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — helps verify client)</span>
              </label>
              <input
                className="form-control"
                style={{ fontFamily: 'monospace' }}
                value={mpesaPhone}
                onChange={e => setMpesaPhone(e.target.value)}
                placeholder="e.g., 0712345678"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Transaction Date
              </label>
              <input
                type="date"
                className="form-control"
                value={mpesaDate}
                onChange={e => setMpesaDate(e.target.value)}
              />
            </div>
          </>
        )}

        {linkType === 'disbursement' && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fbbf24',
            borderRadius: 8, padding: 12, marginBottom: 16,
            fontSize: 13, color: '#92400e',
          }}>
            💡 <strong>Tip:</strong> You only need the M-Pesa receipt code. In the next step,
            you'll select which failed/pending disbursement to update.
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(why was this not auto-linked?)</span>
          </label>
          <textarea
            className="form-control"
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g., C2B callback failed, client brought SMS to counter..."
          />
        </div>
      </div>

      <div className="flex gap-2" style={{ justifyContent: 'space-between', marginTop: 20 }}>
        <button className="btn btn-outline" onClick={() => { setCurrentStep(0); setLinkType(''); }}>
          ← Back
        </button>
        <button className="btn btn-primary"
          disabled={!mpesaCode.trim() || (linkType === 'repayment' && (!mpesaAmount || parseFloat(mpesaAmount) <= 0))}
          onClick={() => setCurrentStep(2)}>
          Find Client / Disbursement →
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: STEP 2 — Find Client (repayment) or Select Disbursement (disbursement)
  // ═══════════════════════════════════════════════════════════
  const renderStep2 = () => {
    if (linkType === 'disbursement') {
      return (
        <div>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 4px 0' }}>Select the Disbursement to Confirm</h4>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
              M-Pesa Code: <strong style={{ fontFamily: 'monospace', color: '#1e40af' }}>{mpesaCode}</strong>
              — Select the failed/pending disbursement that actually succeeded.
            </p>
          </div>

          {loadingPending ? (
            <div className="text-center p-5">
              <span style={{ fontSize: 24, animation: 'mpesaBlink 1s infinite' }}>⏳</span>
              <div className="text-muted mt-2">Loading pending disbursements...</div>
            </div>
          ) : pendingDisbursements.length === 0 ? (
            <div className="text-center p-5">
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 600, color: '#475569', marginBottom: 4 }}>No Failed/Pending Disbursements</div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>
                There are no disbursements with failed or pending status to link.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 700, margin: '0 auto' }}>
              {pendingDisbursements.map(disb => {
                const isSelected = selectedDisbursement?._id === disb._id;
                return (
                  <button
                    key={disb._id}
                    onClick={() => {
                      setSelectedDisbursement(disb);
                      setCurrentStep(3);
                    }}
                    style={{
                      padding: 16, borderRadius: 10,
                      border: `2px solid ${isSelected ? '#2563eb' : '#e2e8f0'}`,
                      background: isSelected ? '#eff6ff' : '#fff',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#93c5fd'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0'; }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{disb.clientName}</div>
                        <div style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>
                          {disb.disbursementPhone} • {disb.clientNumber}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#1e40af' }}>
                          {formatCurrency(disb.disbursementAmount)}
                        </div>
                        <DisbursementStatusBadge status={disb.status} />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                      Initiated: {formatDate(disb.initiatedAt)} by {disb.initiatorName}
                      {disb.resultDesc && (
                        <span style={{ color: '#dc2626' }}> • Error: {disb.resultDesc}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2" style={{ justifyContent: 'flex-start', marginTop: 20 }}>
            <button className="btn btn-outline" onClick={() => setCurrentStep(1)}>← Back</button>
          </div>
        </div>
      );
    }

    // Repayment: search for client
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 4px 0' }}>Find the Client</h4>
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
            Search by name, phone, National ID, or client number to find who made this payment.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', borderRadius: 20,
            background: '#ecfdf5', border: '1px solid #34d399',
            fontSize: 12, fontWeight: 600, color: '#059669',
            marginTop: 8,
          }}>
            Code: <strong style={{ fontFamily: 'monospace' }}>{mpesaCode}</strong> •
            Amount: <strong>{formatCurrency(parseFloat(mpesaAmount) || 0)}</strong>
            {mpesaPhone && <> • Phone: <strong style={{ fontFamily: 'monospace' }}>{mpesaPhone}</strong></>}
          </div>
        </div>

        {/* Search Input */}
        <div style={{ maxWidth: 500, margin: '0 auto 16px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>🔍</span>
          <input
            className="form-control"
            style={{ paddingLeft: 36, fontSize: 15, padding: '12px 12px 12px 36px' }}
            placeholder="Type name, phone, ID number..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searching && (
            <span style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              animation: 'mpesaBlink 0.8s infinite',
            }}>⏳</span>
          )}
        </div>

        {/* Phone Match Hint */}
        {mpesaPhone && searchQuery === '' && (
          <div style={{
            maxWidth: 500, margin: '0 auto 16px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderRadius: 8, padding: '10px 14px', fontSize: 13,
          }}>
            💡 Start typing to search, or the phone number <strong style={{ fontFamily: 'monospace' }}>{mpesaPhone}</strong> will be used to verify the client.
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{
            maxWidth: 600, margin: '0 auto',
            border: '1px solid #e2e8f0', borderRadius: 10,
            overflow: 'hidden', background: '#fff',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              padding: '8px 14px', background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              fontSize: 12, fontWeight: 600, color: '#64748b',
            }}>
              {searchResults.length} client(s) found
            </div>
            {searchResults.map(client => {
              const phoneMatch = mpesaPhone && client.phone?.includes(mpesaPhone.replace(/^0/, '254').slice(-9));
              return (
                <button
                  key={client._id}
                  onClick={() => handleSelectClient(client)}
                  style={{
                    width: '100%', padding: '12px 14px', border: 'none',
                    borderBottom: '1px solid #f1f5f9',
                    background: phoneMatch ? '#f0fdf4' : '#fff',
                    cursor: 'pointer', textAlign: 'left',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.background = phoneMatch ? '#f0fdf4' : '#fff'}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {client.firstName} {client.lastName}
                      {phoneMatch && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, padding: '1px 6px',
                          borderRadius: 8, background: '#dcfce7', color: '#166534',
                          fontWeight: 700,
                        }}>✓ PHONE MATCH</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {client.clientNumber} • {client.phone}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {client.branch?.name || ''}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* No results */}
        {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
          <div className="text-center p-4" style={{ color: '#94a3b8', fontSize: 14 }}>
            No clients found matching "<strong>{searchQuery}</strong>"
          </div>
        )}

        <div className="flex gap-2" style={{ justifyContent: 'flex-start', marginTop: 20 }}>
          <button className="btn btn-outline" onClick={() => setCurrentStep(1)}>← Back</button>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: STEP 3 — Confirm & Link
  // ═══════════════════════════════════════════════════════════
  const renderStep3 = () => {
    if (linkType === 'repayment') {
      return (
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 4px 0' }}>Confirm Repayment Linking</h4>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
              Review the details below and confirm to record this payment.
            </p>
          </div>

          {/* M-Pesa Transaction Info */}
          <div style={{
            background: '#ecfdf5', border: '1px solid #34d399',
            borderRadius: 10, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, color: '#065f46', marginBottom: 10, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              📱 M-Pesa Transaction
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
              <div>
                <span style={{ color: '#047857', fontSize: 12 }}>Transaction Code</span>
                <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 16 }}>{mpesaCode}</div>
              </div>
              <div>
                <span style={{ color: '#047857', fontSize: 12 }}>Amount</span>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#065f46' }}>{formatCurrency(parseFloat(mpesaAmount))}</div>
              </div>
              <div>
                <span style={{ color: '#047857', fontSize: 12 }}>Phone</span>
                <div style={{ fontFamily: 'monospace' }}>{mpesaPhone || '—'}</div>
              </div>
              <div>
                <span style={{ color: '#047857', fontSize: 12 }}>Date</span>
                <div>{mpesaDate}</div>
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 10, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, color: '#334155', marginBottom: 10, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              👤 Client
            </div>
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedClient.firstName} {selectedClient.lastName}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>
                {selectedClient.clientNumber} • {selectedClient.phone}
              </div>
            </div>
          </div>

          {/* Loan Selection */}
          <div style={{
            background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 10, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, color: '#334155', marginBottom: 10, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              📋 Select Loan to Apply Payment
            </div>
            {clientLoans.length === 0 ? (
              <div style={{ color: '#dc2626', fontSize: 13, padding: 8, background: '#fef2f2', borderRadius: 6 }}>
                ⚠️ This client has no active loans. Cannot link repayment.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {clientLoans.map(loan => {
                  const isSelected = selectedLoan?._id === loan._id;
                  return (
                    <button
                      key={loan._id}
                      onClick={() => setSelectedLoan(loan)}
                      style={{
                        padding: 12, borderRadius: 8,
                        border: `2px solid ${isSelected ? '#059669' : '#e2e8f0'}`,
                        background: isSelected ? '#f0fdf4' : '#f8fafc',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>{loan.loanNumber}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Principal: {formatCurrency(loan.principalAmount)} •
                            Disbursed: {formatDate(loan.disbursementDate)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 800, color: '#dc2626', fontSize: 15 }}>
                            {formatCurrency(loan.totalOutstanding)}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>outstanding</div>
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{
                          marginTop: 8, padding: '6px 10px', borderRadius: 6,
                          background: '#dcfce7', fontSize: 12, color: '#166534',
                        }}>
                          ✓ Payment of <strong>{formatCurrency(parseFloat(mpesaAmount))}</strong> will be applied
                          to this loan using the standard waterfall (Penalty → Interest → Principal).
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Warning */}
          <div style={{
            background: '#fffbeb', border: '1px solid #fbbf24',
            borderRadius: 8, padding: 12, marginBottom: 16,
            fontSize: 12, color: '#92400e',
          }}>
            ⚠️ <strong>Important:</strong> This will create a repayment record with the M-Pesa code
            as the payment reference. The payment will be applied using the interest waterfall
            (Penalty → Interest → Principal) based on the current tiered rate. This action is
            audited and cannot be easily reversed.
          </div>

          <div className="flex gap-2" style={{ justifyContent: 'space-between' }}>
            <button className="btn btn-outline" onClick={() => { setCurrentStep(2); setSelectedLoan(null); }}>
              ← Back
            </button>
            <button
              className="btn btn-primary"
              style={{ padding: '10px 24px', fontSize: 15 }}
              disabled={!canSubmit() || submitting}
              onClick={handleSubmit}
            >
              {submitting ? '⏳ Linking...' : '✅ Confirm & Link Payment'}
            </button>
          </div>
        </div>
      );
    }

    // Disbursement confirmation
    return (
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 4px 0' }}>Confirm Disbursement Update</h4>
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
            Confirm that this disbursement actually succeeded on Safaricom.
          </p>
        </div>

        {selectedDisbursement && (
          <>
            <div style={{
              background: '#eff6ff', border: '1px solid #60a5fa',
              borderRadius: 10, padding: 16, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 10, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                💸 Disbursement Details
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
                <div>
                  <span style={{ color: '#1e40af', fontSize: 12 }}>Client</span>
                  <div style={{ fontWeight: 700 }}>{selectedDisbursement.clientName}</div>
                </div>
                <div>
                  <span style={{ color: '#1e40af', fontSize: 12 }}>Amount</span>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#1e40af' }}>
                    {formatCurrency(selectedDisbursement.disbursementAmount)}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#1e40af', fontSize: 12 }}>Phone</span>
                  <div style={{ fontFamily: 'monospace' }}>{selectedDisbursement.disbursementPhone}</div>
                </div>
                <div>
                  <span style={{ color: '#1e40af', fontSize: 12 }}>Current Status</span>
                  <DisbursementStatusBadge status={selectedDisbursement.status} />
                </div>
              </div>
            </div>

            <div style={{
              background: '#ecfdf5', border: '1px solid #34d399',
              borderRadius: 10, padding: 16, marginBottom: 16,
            }}>
              <div style={{ fontWeight: 700, color: '#065f46', marginBottom: 8, fontSize: 13 }}>
                📱 M-Pesa Confirmation Code
              </div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 22, color: '#065f46' }}>
                {mpesaCode}
              </div>
            </div>

            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 8, padding: 12, marginBottom: 16,
              fontSize: 12, color: '#991b1b',
            }}>
              ⚠️ <strong>Warning:</strong> This will update the disbursement status to "success"
              and mark the loan as disbursed. Make sure the client has confirmed receiving the money
              on their phone. This action is irreversible and fully audited.
            </div>

            <div className="flex gap-2" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-outline" onClick={() => setCurrentStep(2)}>← Back</button>
              <button
                className="btn btn-primary"
                style={{ padding: '10px 24px', fontSize: 15 }}
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? '⏳ Confirming...' : '✅ Confirm Disbursement Success'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // SUCCESS MODAL
  // ═══════════════════════════════════════════════════════════
  const renderSuccessModal = () => (
    <Modal isOpen={showSuccessModal} onClose={handleReset} title="" size="460px">
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
        <h4 style={{ color: '#059669', margin: '0 0 8px 0' }}>Successfully Linked!</h4>
        <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px 0' }}>
          The M-Pesa transaction has been linked to the {linkType}.
        </p>

        {result && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 10, padding: 16, textAlign: 'left', marginBottom: 20,
          }}>
            {result.repayment && (
              <>
                <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 8 }}>
                  PAYMENT RECORDED
                </div>
                <div style={{ fontSize: 13, lineHeight: 2 }}>
                  <div><strong>Repayment ID:</strong> {result.repayment._id}</div>
                  <div><strong>Amount Applied:</strong> {formatCurrency(result.appliedAmount)}</div>
                  <div><strong>Principal:</strong> {formatCurrency(result.principalAmount)}</div>
                  <div><strong>Interest:</strong> {formatCurrency(result.interestAmount)}</div>
                  <div><strong>Penalty:</strong> {formatCurrency(result.penaltyAmount)}</div>
                  <div><strong>Rate at Payment:</strong> {result.interestRateApplied}% (Day {result.interestDaysUsed})</div>
                  <div><strong>Loan Cleared:</strong> {result.loanCleared ? '✅ YES' : '❌ No'}</div>
                  <div><strong>Remaining Balance:</strong> {formatCurrency(result.remainingBalance)}</div>
                </div>
              </>
            )}
            {result.disbursement && (
              <>
                <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600, marginBottom: 8 }}>
                  DISBURSEMENT CONFIRMED
                </div>
                <div style={{ fontSize: 13, lineHeight: 2 }}>
                  <div><strong>Loan Number:</strong> {result.disbursement.loanNumber}</div>
                  <div><strong>Status:</strong> {result.disbursement.status}</div>
                  <div><strong>M-Pesa Receipt:</strong> {result.disbursement.mpesaReceipt}</div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex gap-2" style={{ justifyContent: 'center' }}>
          <button className="btn btn-outline" onClick={handleReset}>
            🔗 Link Another Code
          </button>
          <button className="btn btn-primary" onClick={() => { handleReset(); navigate('/mpesa'); }}>
            ← Back to M-Pesa
          </button>
        </div>
      </div>
    </Modal>
  );

  // ═══════════════════════════════════════════════════════════
  // Disbursement status badge (local)
  // ═══════════════════════════════════════════════════════════
  const DisbursementStatusBadge = ({ status }) => {
    const map = {
      initiated:  { label: '⏳ Initiated',  color: '#6366f1', bg: '#eef2ff', border: '#a5b4fc' },
      processing: { label: '🔄 Processing', color: '#d97706', bg: '#fffbeb', border: '#fbbf24' },
      success:    { label: '✅ Success',    color: '#059669', bg: '#ecfdf5', border: '#34d399' },
      failed:     { label: '❌ Failed',     color: '#dc2626', bg: '#fef2f2', border: '#f87171' },
      cancelled:  { label: '🚫 Cancelled',  color: '#6b7280', bg: '#f9fafb', border: '#9ca3af' },
    };
    const s = map[status] || map.initiated;
    return (
      <span style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 20,
        fontWeight: 600, color: s.color, background: s.bg,
        border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
      }}>
        {s.label}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 style={{ margin: 0 }}>🔗 Link M-Pesa Code to Client</h3>
          <p style={{ margin: '2px 0 0 0', fontSize: 13, color: '#64748b' }}>
            For transactions that didn't auto-link to the system
          </p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/mpesa')}>
          ← Back to M-Pesa Transactions
        </button>
      </div>

      {/* Card */}
      <div className="card" style={{ padding: 24 }}>
        <StepIndicator currentStep={currentStep} steps={steps} />

        {currentStep === 0 && renderStep0()}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>

      {renderSuccessModal()}

      <style>{`
        @keyframes mpesaBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}